import { ExportButton } from '@/common/buttons/ExportButton';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const LogFile = () => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const gridRef = useRef();
  const filterModelRef = useRef({});
  const countRef = useRef(null);

  const [fieldDefs, setFieldDefs] = useState();
  const [exportReq, setExportReq] = useState();

  const pluginCode = 'LOG_FILE_VIEWER_PLUGIN';
  const entity = 'LOG_FILE';

  useEffect(() => {
    rsrc.viewerFieldDefs(module, pluginCode, {}, entity).then(fieldDefs => {
      const nextFieldDefs = fieldDefs.map(fieldDef => {
        if (fieldDef.field === 'level') {
          return {
            ...fieldDef,
            type: 'NONE',
            cellRenderer: 'loggerEffectiveCellRenderer',
          };
        }
        if (fieldDef.field === 'logMessage') {
          return {
            ...fieldDef,
            type: 'NONE',
            cellRenderer: 'logMessageCellRenderer',
          };
        }
        return fieldDef;
      });
      setFieldDefs(nextFieldDefs);
    });
  }, [module]);

  const getGridApi = () => gridRef.current?.gridApiRef?.current;

  const getCurrentExpr = useCallback(() => {
    return ExprUtils.filterModelToExpr(filterModelRef.current, fieldDefs);
  }, [fieldDefs]);

  const buildDataSource = useCallback(() => {
    return {
      getRows: (params) => {
        filterModelRef.current = params.filterModel ?? {};
        const expr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

        const reqParam = {
          startRow: params.startRow,
          endRow: params.endRow,
          expr,
        };

        buildAndSetExportReq(reqParam);

        Promise.all([
          rsrc.viewerRows(module, pluginCode, reqParam, entity),
          rsrc.viewerCount(module, pluginCode, reqParam, entity)
        ]).then(([rows, count]) => {
          const totalCount = count ?? 0;
          countRef.current = totalCount;
          params.successCallback(rows, totalCount);
          gridRef.current.setCount(totalCount);
          gridRef.current.resize();
        }).catch(() => {
          params.failCallback();
        });
      }
    };
  }, [entity, fieldDefs, module, pluginCode]);

  const handleRefresh = useCallback(() => {
    const api = getGridApi();
    if (!api) return;
    const oldCount = countRef.current ?? 0;
    const lastVisible = api.getLastDisplayedRowIndex();
    const wasAtBottom = oldCount > 0 && lastVisible >= oldCount - 1;
    const expr = getCurrentExpr();
    const countReq = {
      startRow: 0,
      endRow: Number.MAX_SAFE_INTEGER,
      expr,
    };
    rsrc.viewerCount(module, pluginCode, countReq, entity)
      .then(count => {
        const totalCount = count ?? 0;
        countRef.current = totalCount;
        api.setRowCount(totalCount, false);
        gridRef.current.setCount(totalCount);
        api.refreshInfiniteCache();
        if (wasAtBottom && totalCount > 0) {
          api.ensureIndexVisible(totalCount - 1, 'bottom');
        }
      })
      .catch(() => {
        api.refreshInfiniteCache();
        if (wasAtBottom && oldCount > 0) {
          api.ensureIndexVisible(oldCount - 1, 'bottom');
        }
      });
  }, [buildDataSource, entity, getCurrentExpr, module, pluginCode, rsrc]);

  const handleGoToFirst = useCallback(() => {
    const api = getGridApi();
    if (!api) return;
    api.ensureIndexVisible(0, 'top');
  }, []);

  const handleGoToLast = useCallback(() => {
    const api = getGridApi();
    if (!api) return;
    const expr = getCurrentExpr();
    const countReq = {
      startRow: 0,
      endRow: Number.MAX_SAFE_INTEGER,
      expr,
    };
    rsrc.viewerCount(module, pluginCode, countReq, entity).then(count => {
      if (count == null || count <= 0) return;
      countRef.current = count;
      api.setRowCount(count, false);
      api.ensureIndexVisible(count - 1, 'bottom');
    });
  }, [entity, getCurrentExpr, module, pluginCode, rsrc]);

  const onGridReady = () => {
    const dataSource = buildDataSource();
    gridRef.current.setDataSource(dataSource);
  };

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      name: 'Log File Export',
      module,
      entity,
      uiType: 'VIEWER',
      viewerPluginCode: pluginCode,
      viewerReqParam: reqParam,
      fieldDefs,
    };

    setExportReq(exportReq);
  };

  return (
    fieldDefs &&
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Title icon={far.faListUl} title="Log File" />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
          <IconButton aria-label="Go to first row" onClick={handleGoToFirst}>
            <FontAwesomeIcon icon={fas.faArrowUp} />
          </IconButton>
          <IconButton aria-label="Go to last row" onClick={handleGoToLast}>
            <FontAwesomeIcon icon={fas.faArrowDown} />
          </IconButton>
          <IconButton aria-label="Refresh" onClick={handleRefresh}>
            <FontAwesomeIcon icon={fas.faRotate} />
          </IconButton>
          <ExportButton exportReq={exportReq} />
        </Box>
      </Box>
      <Grid
        ref={gridRef}
        gridOptions={{ fieldDefs }}
        events={{ onGridReady }}
      />
    </>
  );
};
